import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment';

export enum DateRestrictionType {
  StartMin = 'StartMin',
  StartMax = 'StartMax',
  EndMin = 'EndMin',
  EndMax = 'EndMax',
}

/**
 * DateRangePipe - Applies business rules to restrict date picker values for program creation
 *
 * Purpose:
 * - Enforces minimum 1-day gap between start and end dates
 * - Prevents selecting dates before today
 * - Ensures end date is never before start date (maintains logical date order)
 * - Limits program duration to maximum 1 year
 * - Provides dynamic min/max constraints based on the other selected date
 *
 * Business Rules Applied:
 * - StartMin: Cannot be before today OR more than 1 year before end date
 * - StartMax: Must be at least 1 day before end date
 * - EndMin: Must be at least 1 day after start date (or tomorrow if no start date)
 * - EndMax: Cannot be more than 1 year after start date
 *
 * Usage:
 * [min]="otherDateValue | dateRange:DateRestrictionType.StartMin"
 * [max]="otherDateValue | dateRange:DateRestrictionType.EndMax"
 *
 * @param otherDateValue - The other date field's value (start date for end restrictions, end date for start restrictions)
 * @param restrictionType - Type of restriction to apply (StartMin, StartMax, EndMin, EndMax)
 * @returns Date object representing the min/max allowed date, or null if no restriction
 */
@Pipe({
  name: 'dateRange',
})
export class DateRangePipe implements PipeTransform {
  transform(
    otherDateValue: Date | string | null | undefined,
    restrictionType: DateRestrictionType,
  ): Date | null {
    const today = moment().startOf('day'); // today at midnight
    const otherDate = otherDateValue ? moment(otherDateValue) : null;

    switch (restrictionType) {
      case DateRestrictionType.StartMin:
        // Start date cannot be before today OR more than 1 year before end date
        const endDateMinusYear = otherDate?.subtract(1, 'year');
        return endDateMinusYear ? moment.max(today, endDateMinusYear).toDate() : today.toDate();

      case DateRestrictionType.StartMax:
        // Start date must be at least 1 day before end date
        return otherDate?.subtract(1, 'day').toDate() || null;

      case DateRestrictionType.EndMin:
        // End date must be at least 1 day after start date if picked, else tomorrow
        const baseDate = otherDate || today;
        return baseDate.add(1, 'day').toDate();

      case DateRestrictionType.EndMax:
        // End date cannot be more than 1 year after start date
        return otherDate?.add(1, 'year').toDate() || null;

      default:
        return null;
    }
  }
}
